import { useState, useEffect, useRef } from "react";

const BRAND = "#10165C";
const BRAND_LIGHT = "#1a2070";
const BRAND_DARK = "#0a0f3d";

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800;900&family=Inter:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --brand: #10165C;
    --brand-light: #1a2070;
    --brand-dark: #0a0f3d;
    --white: #ffffff;
    --black: #0a0a0a;
    --gray-100: #f5f5f5;
    --gray-200: #e8e8e8;
    --gray-300: #d0d0d0;
    --gray-500: #888;
    --gray-700: #444;
    --font-head: 'Lexend', sans-serif;
    --font-body: 'Inter', sans-serif;
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: var(--font-body);
    background: var(--white);
    color: var(--black);
    overflow-x: hidden;
  }

  .lexend { font-family: var(--font-head); }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideLeft {
    from { opacity: 0; transform: translateX(60px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes scaleUp {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes lineGrow {
    from { width: 0; }
    to { width: 100%; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  @keyframes marquee {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @keyframes countUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .animate-fadeUp { animation: fadeUp 0.8s ease forwards; }
  .animate-fadeIn { animation: fadeIn 0.6s ease forwards; }
  .animate-scaleUp { animation: scaleUp 0.7s ease forwards; }

  .hero {
    min-height: 100vh;
    background: var(--brand);
    display: flex; align-items: center; justify-content: center;
    position: relative; overflow: hidden;
    padding: clamp(40px, 8vw, 80px) clamp(16px, 4vw, 5%);
    overflow:hidden;
  }
  .hero-bg-text {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    font-family: var(--font-head); font-size: 30vw; font-weight: 700;
    color: rgba(255,255,255,0.025); white-space: nowrap; pointer-events: none;
    user-select: none; letter-spacing: -0.05em;
  }
  .hero-grid-canvas {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    pointer-events: none; z-index: 1;
  }
  .hero-orb1 {
    position: absolute; width: 600px; height: 600px;
    border-radius: 50%; background: rgba(255,255,255,0.04);
    top: -200px; right: -200px; animation: float 8s ease-in-out infinite;
  }
  .hero-orb2 {
    position: absolute; width: 300px; height: 300px;
    border-radius: 50%; background: rgba(255,255,255,0.06);
    bottom: -100px; left: 10%; animation: float 6s ease-in-out infinite 2s;
  }
  .hero-content {
    position: relative; z-index: 2; max-width: 900px;
    text-align: center;
  }
  .hero-eyebrow {
    display: inline-flex; align-items: center; gap: 10px;
    background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2);
    border-radius: 100px; padding: 8px 20px; margin-bottom: 32px;
    opacity: 0; animation: fadeUp 0.8s ease 0.2s forwards;
  }
  .hero-eyebrow-dot {
    width: 7px; height: 7px; background: #4af; border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }
  .hero-eyebrow span {
    font-family: var(--font-body); font-size: 13px; font-weight: 500;
    color: rgba(255,255,255,0.85); letter-spacing: 0.05em; text-transform: uppercase;
  }
  .hero-title {
    font-family: var(--font-head); font-size: clamp(2.8rem, 6vw, 5.5rem);
    font-weight: 800; color: white; line-height: 1.05;
    letter-spacing: -0.03em; margin-bottom: 28px;
    opacity: 0; animation: fadeUp 0.9s ease 0.4s forwards;
  }
  .hero-title .accent { color: #60aaff; }
  .hero-sub {
    font-family: var(--font-body); font-size: clamp(1rem, 2vw, 1.2rem);
    font-weight: 400; color: rgba(255,255,255,0.7); max-width: 640px;
    margin: 0 auto 48px; line-height: 1.7;
    opacity: 0; animation: fadeUp 1s ease 0.6s forwards;
  }
  .hero-btns {
    display: flex; gap: 16px; justify-content: center; flex-wrap: wrap;
    opacity: 0; animation: fadeUp 1s ease 0.8s forwards;
  }
  .btn-primary {
    background: white; color: var(--brand);
    font-family: var(--font-head); font-size: 15px; font-weight: 700;
    padding: 16px 36px; border-radius: 8px; border: none; cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(0,0,0,0.2); }
  .btn-secondary {
    background: transparent; color: white;
    font-family: var(--font-head); font-size: 15px; font-weight: 600;
    padding: 15px 36px; border-radius: 8px;
    border: 1.5px solid rgba(255,255,255,0.4); cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
  }
  .btn-secondary:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.7); }
  .hero-trust {
    margin-top: 20px; font-family: var(--font-body); font-size: 13px;
    color: rgba(255,255,255,0.45); letter-spacing: 0.04em;
    opacity: 0; animation: fadeUp 1s ease 1s forwards;
  }

  .hero-scroll {
    position: absolute; bottom: 23px; left: 50%; transform: translateX(-50%);
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    opacity: 0; animation: fadeIn 1s ease 1.5s forwards;
  }
  .hero-scroll span {
    font-family: var(--font-body); font-size: 11px; font-weight: 500;
    color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.1em;
  }
  .scroll-line {
    width: 1px; height: 40px; background: rgba(255,255,255,0.2);
    position: relative; overflow: hidden;
  }
  .scroll-line::after {
    content: ''; position: absolute; top: -100%;
    width: 100%; height: 100%; background: rgba(255,255,255,0.6);
    animation: scrollDown 1.5s ease-in-out infinite;
  }
  @keyframes scrollDown {
    to { top: 200%; }
  }

  .marquee-section {
    background: var(--brand-dark); padding: 22px 0; overflow: hidden;
    border-top: 1px solid rgba(255,255,255,0.05);
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .marquee-track {
    display: flex; gap: 60px; white-space: nowrap;
    animation: marquee 20s linear infinite;
  }
  .marquee-item {
    font-family: var(--font-head); font-size: 13px; font-weight: 600;
    color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 0.12em;
    flex-shrink: 0; display: flex; align-items: center; gap: 16px;
  }
  .marquee-sep { width: 4px; height: 4px; border-radius: 50%; background: rgba(255,255,255,0.2); }

  section { padding: clamp(60px, 10vw, 100px) clamp(16px, 4vw, 5%); }
  .section-label {
    display: inline-block; font-family: var(--font-body); font-size: 12px;
    font-weight: 600; color: var(--brand); letter-spacing: 0.12em;
    text-transform: uppercase; margin-bottom: 16px;
    position: relative; padding-bottom: 12px;
  }
  .section-label::after {
    content: ''; position: absolute; bottom: 0; left: 0;
    width: 32px; height: 2px; background: var(--brand);
  }
  .section-title {
    font-family: var(--font-head); font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800; color: var(--black); line-height: 1.1;
    letter-spacing: -0.025em; margin-bottom: 20px;
  }
  .section-sub {
    font-family: var(--font-body); font-size: 1.05rem; color: var(--gray-500);
   line-height: 1.7; margin-bottom: 60px;
  }

  .case-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(clamp(280px, 90vw, 320px), 1fr));
    gap: clamp(20px, 5vw, 28px);
  }
  .case-card {
    background: white; border-radius: 16px; overflow: hidden;
    border: 1px solid var(--gray-200);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: pointer;
    min-height:stretch;
  }
  .case-card:hover { transform: translateY(-8px); box-shadow: 0 24px 60px rgba(16,22,92,0.12); }
  .case-card-top {
    padding: 32px 28px 24px;
    position: relative; overflow: hidden;
  }
  .case-card-industry {
    font-family: var(--font-body); font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.1em;
    padding: 5px 12px; border-radius: 100px;
    display: inline-block; margin-bottom: 16px;
  }
  .industry-realestate { background: #eef2ff; color: #3949ab; }
  .industry-retail { background: #fce4ec; color: #c62828; }
  .industry-food { background: #e8f5e9; color: #2e7d32; }
  .industry-fitness { background: #fff3e0; color: #e65100; }
  .case-card-title {
    font-family: var(--font-head); font-size: 1.3rem; font-weight: 700;
    color: var(--black); margin-bottom: 12px; line-height: 1.2;
  }
  .case-card-desc {
    font-family: var(--font-body); font-size: 0.9rem; color: var(--gray-500);
    line-height: 1.6; margin-bottom: 20px;
  }
  .case-results {
    display: flex; flex-direction: column; gap: 8px; margin-bottom: 0;
  }
  .case-result-item {
    display: flex; align-items: center; gap: 10px;
    font-family: var(--font-body); font-size: 0.85rem; color: var(--gray-700);
  }
  .case-result-icon {
    width: 20px; height: 20px; border-radius: 50%;
    background: var(--brand); display: flex; align-items: center;
    justify-content: center; flex-shrink: 0;
  }
  .case-result-icon svg { width: 10px; height: 10px; }
  .case-card-bottom {
    padding: 20px 28px;
    border-top: 1px solid var(--gray-200);
    display: flex; align-items: center; justify-content: space-between;
  }
  .case-client { display: flex; align-items: center; }
  .case-client-logo { height: 32px; max-width: 110px; width: auto; object-fit: contain; display: block; }
  .case-view-btn {
    font-family: var(--font-body); font-size: 13px; font-weight: 600;
    color: var(--brand); background: none; border: 1px solid var(--brand);
    padding: 8px 18px; border-radius: 6px; cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }
  .case-view-btn:hover { background: var(--brand); color: white; }
  .case-card-number {
    position: absolute; right: 20px; top: 20px;
    font-family: var(--font-head); font-size: 5rem; font-weight: 900;
    color: rgba(16,22,92,0.04); line-height: 1; pointer-events: none;
  }

  .industries-section { background: var(--gray-100); }
  .industry-grid {
    display: flex;
    // grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    justify-content:center;
    gap: clamp(12px, 4vw, 20px);
    align-items: stretch;
    flex-wrap:wrap;
  }
  .industry-card {
    background: white; border-radius: 14px; padding: 28px;
    border: 1px solid var(--gray-200);
    transition: transform 0.3s, box-shadow 0.3s;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
    height: 100%;
    width:300px;
  }
  .industry-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(16,22,92,0.08); }
  .industry-icon {
    width: 100%; height: 120px; border-radius: 10px;
    display: flex; align-items: center;
    justify-content: center; margin-bottom: 18px; overflow: hidden;
    border:1px solid #10165c4f;
  }
  .industry-icon img {
    width: 100%; height: 100%; object-fit: contain; padding: 14px;
  }
  .industry-name {
    font-family: var(--font-head); font-size: 1.05rem; font-weight: 700;
    color: var(--black); margin-bottom: 10px;
  }
  .industry-desc {
    font-family: var(--font-body); font-size: 0.88rem; color: var(--gray-500);
    line-height: 1.6;
  }

  /* ── TESTIMONIALS SECTION ── */
  .testimonials-section { background: var(--brand); }
  .testimonials-section .section-label { color: rgba(255,255,255,0.6); }
  .testimonials-section .section-label::after { background: rgba(255,255,255,0.4); }
  .testimonials-section .section-title { color: white; }
  .testimonials-section .section-sub { color: rgba(255,255,255,0.6); }

  /* VIDEO ROW */
  .video-row {
    display: flex; gap: 14px; overflow-x: auto; padding-bottom: 8px;
    scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch;
    scrollbar-width: none; margin-bottom: 64px;
    justify-content:center;
  }
  .video-row::-webkit-scrollbar { display: none; }
  .video-card {
    flex-shrink: 0; width: 190px; cursor: pointer;
    scroll-snap-align: start; transition: transform 0.3s;
  }
  .video-card:hover { transform: scale(1.03); }
  .video-thumb {
    width: 100%; aspect-ratio: 9/16; border-radius: 16px; overflow: hidden;
    position: relative; background: linear-gradient(160deg,rgba(255,255,255,0.1) 0%,rgba(255,255,255,0.03) 100%);
    border: 1px solid rgba(255,255,255,0.14);
  }
  .video-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .video-thumb-placeholder {
    width: 100%; height: 100%; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 12px;
  }
  .video-insta-icon { opacity: 0.3; }
  .video-play-overlay {
    position: absolute; inset: 0; display: flex;
    align-items: center; justify-content: center;
    background: rgba(0,0,0,0.18);
    transition: background 0.2s;
  }
  .video-card:hover .video-play-overlay { background: rgba(0,0,0,0.30); }
  .video-play-circle {
    width: 52px; height: 52px; border-radius: 50%;
    background: rgba(255,255,255,0.95); display: flex;
    align-items: center; justify-content: center;
    box-shadow: 0 4px 24px rgba(0,0,0,0.35);
    transition: transform 0.25s, box-shadow 0.25s;
  }
  .video-card:hover .video-play-circle { transform: scale(1.12); box-shadow: 0 8px 32px rgba(0,0,0,0.45); }
  .video-card-info { padding: 10px 2px 0; }
  .video-card-name {
    font-family: var(--font-head); font-size: 0.82rem; font-weight: 700;
    color: white; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .video-card-role {
    font-family: var(--font-body); font-size: 0.72rem; color: rgba(255,255,255,0.5);
    margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  /* VIDEO MODAL */
  .video-modal-overlay {
    position: fixed; inset: 0; z-index: 3000;
    background: rgba(0,0,0,0.88); backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px; animation: fadeIn 0.2s ease;
  }
  .video-modal {
    position: relative; width: 100%; max-width: 380px;
    border-radius: 18px; overflow: hidden;
    background: #000; animation: scaleUp 0.3s ease;
    box-shadow: 0 32px 80px rgba(0,0,0,0.6);
  }
  .video-modal-close {
    position: absolute; top: 12px; right: 12px; z-index: 10;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(0,0,0,0.65); border: 1.5px solid rgba(255,255,255,0.2);
    cursor: pointer; color: white; font-size: 22px; line-height: 1;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s;
  }
  .video-modal-close:hover { background: rgba(0,0,0,0.9); }
  .video-modal-frame {
    width: 100%; aspect-ratio: 9/16; display: block; border: none;
  }

  /* REVIEW MARQUEE */
  .review-marquee-outer {
    overflow: hidden;
    -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
    mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
  }
  .review-marquee-track {
    display: flex; gap: 18px; width: max-content;
    animation: reviewScroll 32s linear infinite;
    margin-top:40px;
  }
  .review-marquee-track:hover { animation-play-state: paused; }
  @keyframes reviewScroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  .review-card {
    flex-shrink: 0; width: 300px; background: white;
    border-radius: 16px; padding: 20px 22px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.10);
  }
  .review-card-top {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
  }
  .review-card-author { display: flex; align-items: center; gap: 10px; }
  .review-card-avatar {
    width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0;
    background: var(--brand); display: flex; align-items: center;
    justify-content: center; font-family: var(--font-head);
    font-size: 14px; font-weight: 700; color: white;
  }
  .review-card-name {
    font-family: var(--font-head); font-size: 0.88rem; font-weight: 700; color: var(--black);
  }
  .review-card-source {
    font-family: var(--font-body); font-size: 0.7rem; color: var(--gray-500); margin-top: 1px;
  }
  .review-stars { display: flex; gap: 2px; margin-bottom: 8px; }
  .review-star { color: #fbbc04; font-size: 14px; }
  .review-text {
    font-family: var(--font-body); font-size: 0.84rem; color: var(--gray-700); line-height: 1.65;
  }

@media (max-width: 768px) {
    /* Comprehensive mobile spacing fix - full-width feel with clamp */
    .hero { 
      padding: clamp(40px, 8vw, 60px) 0 !important; 
      margin: 0 !important;
    }
    .hero-content { 
      padding: 0 clamp(8px, 4vw, 16px) !important; 
      width: 100vw; 
      max-width: 100vw; 
      margin-left: calc(50% - 50vw + clamp(8px, 4vw, 16px)) !important;
    }
    
    /* Sections full viewport width */
    section { 
      padding: clamp(50px, 12vw, 80px) clamp(8px, 4vw, 16px) !important; 
      margin: 0 !important;
      width: 100vw;
      max-width: 100vw;
      margin-left: calc(50% - 50vw) !important;
    }
    
    /* AOS/Animation fixes - reduce transform to minimize side space */
    .observe-fade {
      transform: translateY(12px) !important;
    }
    .observe-fade.visible {
      transform: none !important;
    }
    .anim-wrap {
      transform: translateY(20px) !important;
      will-change: auto !important;
    }
    .anim-wrap.visible {
      transform: none !important;
    }
    
    /* Direct child content tight spacing */
    section > *:first-child { padding-top: 0 !important; }
    .section-title, .section-sub { margin-left: 0 !important; margin-right: 0 !important; }
    
    /* Hero bg-text */
    .hero-bg-text { 
      font-size: clamp(22vw, 35vw, 30vw) !important; 
      left: 50vw !important;
      transform: translateX(-50%) translateY(-50%) !important;
    }
    
    /* Grids - zero side margins, full stretch */
    .industry-grid {
      grid-template-columns: 1fr !important;
      gap: clamp(16px, 5vw, 24px) !important;
      width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .industry-card {
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      border-radius: 12px !important;
      padding: clamp(20px, 6vw, 28px) !important;
    }
    
    .case-grid {
      grid-template-columns: 1fr !important;
      gap: clamp(20px, 6vw, 32px) !important;
      width: 100% !important;
      padding: 0 !important;
    }
    .case-card { 
      margin: 0 !important; 
      border-radius: 16px !important;
      width: 100% !important;
    }
    
    .services-grid {
      grid-template-columns: 1fr !important;
      gap: clamp(20px, 6vw, 32px) !important;
      width: 100% !important;
      padding: 0 !important;
    }
    .service-card { 
      margin: 0 !important; 
      border-radius: 16px !important;
      width: 100% !important;
      padding: clamp(24px, 8vw, 32px) !important;
    }
    
    /* Video row tight */
    .video-row { 
      padding: 0 clamp(8px, 4vw, 20px) !important;
      margin: 0 !important;
      gap: clamp(12px, 4vw, 20px) !important;
    }
    .video-card { 
      width: clamp(140px, 38vw, 170px) !important; 
      flex-shrink: 0;
    }
    
    /* Stats tight */
    .stats-row { 
      grid-template-columns: repeat(2, 1fr) !important; 
      margin: clamp(8px, 3vw, 20px) 0 !important;
      gap: 2px !important;
      border-radius: 12px !important;
    }
    
    /* CTA section */
    .cta-section { 
      padding: clamp(60px, 14vw, 100px) clamp(12px, 5vw, 24px) !important;
      margin: 0 !important;
    }
    
    /* Marquee */
    .marquee-track { gap: clamp(32px, 8vw, 50px) !important; }
    
    /* Modals/popups */
    .modal-body { padding: clamp(20px, 6vw, 36px) clamp(16px, 5vw, 42px) !important; }
    .modal-header { padding: clamp(24px, 7vw, 36px) clamp(16px, 5vw, 42px) !important; }
    .cta-popup { 
      margin: clamp(16px, 6vw, 32px) !important;
      padding: clamp(24px, 8vw, 36px) clamp(16px, 6vw, 32px) !important;
    }
  }
  .services-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(clamp(260px, 90vw, 300px), 1fr));
    gap: clamp(18px, 4vw, 24px);
  }
  .service-card {
    border-radius: 16px; padding: 32px;
    border: 1px solid var(--gray-200); position: relative; overflow: hidden;
    transition: transform 0.3s, border-color 0.3s;
    min-height:stretch;
  }
  .service-card:hover { transform: translateY(-6px); border-color: var(--brand); }
  .service-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(16,22,92,0.03) 0%, transparent 60%);
    opacity: 0; transition: opacity 0.3s;
  }
  .service-card:hover::before { opacity: 1; }
  .service-number {
    font-family: var(--font-head); font-size: 0.75rem; font-weight: 700;
    color: var(--brand); letter-spacing: 0.12em; opacity: 0.4; margin-bottom: 20px;
  }
  .service-icon {
    width: 100%; height: 130px; border-radius: 10px;
    background: var(--gray-100); display: flex; align-items: center;
    justify-content: center; margin-bottom: 18px; overflow: hidden;
  }
  .service-icon img {
    width: 100%; height: 100%; object-fit: contain; padding: 14px;
  }
  .service-name {
    font-family: var(--font-head); font-size: 1.1rem; font-weight: 700;
    color: var(--black); margin-bottom: 12px;
  }
  .service-desc {
    font-family: var(--font-body); font-size: 0.9rem; color: var(--gray-500);
    line-height: 1.65;
  }
  .service-line {
    position: absolute; bottom: 0; left: 0; height: 3px;
    background: var(--brand); width: 0; transition: width 0.4s ease;
  }
  .service-card:hover .service-line { width: 100%; }

  .stats-row {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 2px; background: var(--gray-200);
    border-radius: 16px; overflow: hidden; margin-bottom: 80px;
  }
  .stat-item {
    background: white; padding: 36px 28px; text-align: center;
  }
  .stat-num {
    font-family: var(--font-head); font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 900; color: var(--brand); line-height: 1;
    margin-bottom: 8px;
  }
  .stat-label {
    font-family: var(--font-body); font-size: 0.85rem; color: var(--gray-500);
    font-weight: 500;
  }

  .cta-section {
    background: var(--black); padding: 120px 5%;
    text-align: center; position: relative; overflow: hidden;
  }
  .cta-section::before {
    content: ''; position: absolute; width: 800px; height: 800px;
    border-radius: 50%; background: radial-gradient(circle, rgba(16,22,92,0.6) 0%, transparent 70%);
    top: 50%; left: 50%; transform: translate(-50%, -50%);
    pointer-events: none;
  }
  .cta-title {
    font-family: var(--font-head); font-size: clamp(2.2rem, 5vw, 4rem);
    font-weight: 900; color: white; margin-bottom: 20px;
    letter-spacing: -0.03em; position: relative; z-index: 1;
  }
  .cta-sub {
    font-family: var(--font-body); font-size: 1.05rem; color: rgba(255,255,255,0.5);
    max-width: 560px; margin: 0 auto 48px; line-height: 1.7;
    position: relative; z-index: 1;
  }
  .cta-btn {
    background: var(--brand); color: white;
    font-family: var(--font-head); font-size: 16px; font-weight: 700;
    padding: 18px 48px; border-radius: 8px; border: none; cursor: pointer;
    position: relative; z-index: 1; transition: transform 0.2s, box-shadow 0.2s;
  }
  .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(16,22,92,0.5); }

  .modal-overlay {
    position: fixed; inset: 0; z-index: 2000;
    background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);
    display: flex; align-items: flex-start; justify-content: center;
    padding: 60px 20px; overflow-y: auto;
  }
  .modal-content {
    background: white; border-radius: 20px; max-width: 800px; width: 100%;
    overflow: hidden; animation: scaleUp 0.3s ease;
  }
  .modal-header {
    background: var(--brand); padding: 40px 48px;
    position: relative;
  }
  .subhead{
    display:flex;
    flex-direction:column;
    justify-content:center;
    align-items:center;  
   }
  .modal-close {
    position: absolute; top: 20px; right: 20px;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(255,255,255,0.1); border: none; cursor: pointer;
    color: white; font-size: 20px; display: flex; align-items: center;
    justify-content: center; transition: background 0.2s;
  }
  .modal-close:hover { background: rgba(255,255,255,0.2); }
  .modal-industry-tag {
    font-family: var(--font-body); font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.1em; color: rgba(255,255,255,0.6);
    margin-bottom: 12px;
  }
  .modal-title {
    font-family: var(--font-head); font-size: clamp(1.5rem, 3vw, 2.2rem);
    font-weight: 800; color: white; margin-bottom: 8px; line-height: 1.15;
  }
  .modal-client {
    font-family: var(--font-body); font-size: 0.95rem; color: rgba(255,255,255,0.6);
  }
  .modal-body { padding: 40px 48px; }
  .modal-section { margin-bottom: 32px; }
  .modal-section-title {
    font-family: var(--font-head); font-size: 1rem; font-weight: 700;
    color: var(--brand); margin-bottom: 12px; text-transform: uppercase;
    letter-spacing: 0.05em; font-size: 0.85rem;
  }
  .modal-text {
    font-family: var(--font-body); font-size: 0.95rem; color: var(--gray-700);
    line-height: 1.75;
  }
  .modal-results {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px; margin-top: 16px;
  }
  .modal-result-item {
    background: var(--gray-100); border-radius: 12px; padding: 20px;
    text-align: center;
  }
  .modal-result-num {
    font-family: var(--font-head); font-size: 1.8rem; font-weight: 900;
    color: var(--brand); line-height: 1;
  }
  .modal-result-label {
    font-family: var(--font-body); font-size: 0.8rem; color: var(--gray-500);
    margin-top: 6px;
  }
  .modal-steps { display: flex; flex-direction: column; gap: 16px; }
  .modal-step {
    display: flex; gap: 16px; align-items: flex-start;
  }
  .modal-step-num {
    width: 32px; height: 32px; border-radius: 50%; background: var(--brand);
    color: white; font-family: var(--font-head); font-size: 0.8rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px;
  }
  .modal-step-text {
    font-family: var(--font-body); font-size: 0.93rem; color: var(--gray-700);
    line-height: 1.65; text-align: left;
  }
  .modal-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
  .modal-tag {
    background: #eef2ff; color: var(--brand); padding: 6px 14px;
    border-radius: 100px; font-size: 0.8rem; font-weight: 600;
    font-family: var(--font-body);
  }

  .visible { opacity: 1 !important; transform: none !important; }

  .observe-fade {
    opacity: 0; transform: translateY(30px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .observe-fade.delay-1 { transition-delay: 0.1s; }
  .observe-fade.delay-2 { transition-delay: 0.2s; }
  .observe-fade.delay-3 { transition-delay: 0.3s; }
  .observe-fade.delay-4 { transition-delay: 0.4s; }

  /* ── CTA POPUP ── */
  .cta-popup-overlay {
    position: fixed; inset: 0; z-index: 4000;
    background: rgba(0,0,0,0.75); backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px; animation: fadeIn 0.22s ease;
  }
  .cta-popup {
    background: white; border-radius: 24px; max-width: 440px; width: 100%;
    padding: 40px 36px 36px; position: relative; text-align: center;
    animation: popIn 0.4s cubic-bezier(0.22,1,0.36,1);
    box-shadow: 0 40px 100px rgba(0,0,0,0.35);
  }
  @keyframes popIn {
    from { opacity: 0; transform: scale(0.82) translateY(30px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  .cta-popup-close {
    position: absolute; top: 16px; right: 16px;
    width: 34px; height: 34px; border-radius: 50%;
    background: var(--gray-100); border: none; cursor: pointer;
    font-size: 18px; display: flex; align-items: center; justify-content: center;
    transition: background 0.2s; color: var(--gray-700);
  }
  .cta-popup-close:hover { background: var(--gray-200); }
  .cta-popup-icon-wrap {
    width: 80px; height: 80px; border-radius: 50%;
    background: #25d366; display: flex; align-items: center; justify-content: center;
    margin: 0 auto 24px; position: relative;
  }
  .cta-popup-icon-wrap::before, .cta-popup-icon-wrap::after {
    content: ''; position: absolute; border-radius: 50%;
    border: 2px solid #25d366; animation: waRing 2s ease-out infinite;
  }
  .cta-popup-icon-wrap::after { animation-delay: 0.7s; }
  @keyframes waRing {
    0%   { width: 80px; height: 80px; opacity: 0.7; }
    100% { width: 140px; height: 140px; opacity: 0; }
  }
  .cta-popup-heading {
    font-family: var(--font-head); font-size: 1.75rem; font-weight: 800;
    color: var(--black); line-height: 1.2; margin-bottom: 12px;
    letter-spacing: -0.02em;
  }
  .cta-popup-sub {
    font-family: var(--font-body); font-size: 0.95rem; color: var(--gray-500);
    line-height: 1.65; margin-bottom: 24px;
  }
  .cta-popup-badges {
    display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-bottom: 28px;
  }
  .cta-popup-badge {
    display: flex; align-items: center; gap: 6px;
    background: var(--gray-100); border-radius: 100px;
    padding: 7px 14px; font-family: var(--font-body);
    font-size: 9px; font-weight: 600; color: var(--gray-700);
    border:1px solid #10165
  }
  .wa-cta-btn {
    width: 100%; padding: 16px 24px; border-radius: 12px;
    background: #25d366; color: white; border: none; cursor: pointer;
    font-family: var(--font-head); font-size: 1rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center; gap: 10px;
    transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 8px 24px rgba(37,211,102,0.35);
    text-decoration:none;
  }
  .wa-cta-btn:hover { background: #1ebe5d; transform: translateY(-2px); box-shadow: 0 14px 32px rgba(37,211,102,0.45); }
  .wa-cta-btn svg { flex-shrink: 0; }

  /* ── SECTION AOS EASING ── */
  .anim-wrap { will-change: opacity, transform; }

  @media (max-width: 768px) {
    .hero-btns { align-items: center; }
    .stats-row { grid-template-columns: repeat(2, 1fr); }
    .modal-body { padding: 28px 24px; }
    .modal-header { padding: 28px 24px; }
    .video-row { justify-content: start; }
    .cta-popup { padding: 32px 20px 28px; }
    .cta-popup-heading { font-size: 1.45rem; }
  }
`;

const caseStudies = [
  {
    id: 1,
    logo: "/kudil.png",
    client: "Kudil",
    project: "VIP Garden",
    industry: "Real Estate",
    industryClass: "industry-realestate",
    description: "Sold 33 out of 35 plots in just 2 months using a structured marketing funnel strategy with community building and exclusive events.",
    results: ["33/35 plots sold in 2 months", "120 WhatsApp group members", "75% sales via funnel strategy", "50+ site visits from funnel"],
    fullResults: [
      { num: "33/35", label: "Plots Sold" },
      { num: "2 Mo", label: "Time Period" },
      { num: "120", label: "WhatsApp Members" },
      { num: "75%", label: "Sales via Funnel" },
    ],
    challenge: "Kudil launched VIP Garden on NH7, Kovilpatti in October 2025. Real estate purchases involve high trust and long decision cycles. The challenge was not just generating awareness — it was building trust and converting interested prospects into actual plot buyers.",
    strategy: "We designed a 3-stage marketing funnel: Awareness → Engagement → Conversion, focusing on nurturing interested prospects instead of chasing random leads.",
    steps: [
      "Launched Early Bird Offer campaign — interested people joined a WhatsApp group open for only 10 days, creating urgency. 120 members joined.",
      "Invited all 120 members to an exclusive mela event with free pickup & drop, creating a sense of exclusivity and trust.",
      "At conversion stage, we ensured full transparency on legal documents, addressed buyer questions, and offered free patta documentation.",
    ],
    tags: ["Branding", "Marketing Strategy", "Content Creation", "Funnel Design", "Lead Nurturing"],
  },
  {
    id: 2,
    logo: "/jeyarajandco.png",
    client: "Jeyaraj & Co",
    project: "Hypermarket Launch",
    industry: "Retail",
    industryClass: "industry-retail",
    description: "Launched a hypermarket opposite D-Mart and built 5M+ video views, 3M+ reach, and strong local brand dominance through a 3-stage campaign.",
    results: ["5M+ cumulative video views", "3M+ people reached", "5,000+ quality followers", "Became recurring client"],
    fullResults: [
      { num: "5M+", label: "Video Views" },
      { num: "3M+", label: "People Reached" },
      { num: "5K+", label: "Quality Followers" },
      { num: "30+", label: "Videos in Month 1" },
    ],
    challenge: "A new hypermarket launching in Kovilpatti — with D-Mart opening directly opposite just days before their launch. The challenge was to build strong brand awareness and stand out despite a major national retail competitor.",
    strategy: "A three-stage strategy: Pre-Launch Curiosity Campaign → Grand Opening Impact Campaign → Post-Launch Consistent Engagement, leveraging local audience psychology.",
    steps: [
      "Pre-launch: Created 10+ curiosity-based videos, banners, and posters — making the entire city curious before the store even opened.",
      "Grand Opening: Produced a high-impact grand opening video campaign (₹50,000 production) that spread rapidly and became the most talked-about video in the city.",
      "Post-launch: 30+ videos in month 1, continuous social media content, and seasonal campaign executions to sustain engagement.",
    ],
    tags: ["Social Media Marketing", "Branding", "Advertising Campaigns", "Video Content"],
  },
  {
    id: 3,
    logo: "/seasuvai.png",
    client: "Seasuvai",
    project: "Organic Brand Growth",
    industry: "Food & Seafood",
    industryClass: "industry-food",
    description: "Built a karuvadu (dry fish) brand from scratch using story-driven content, visual storytelling from fishermen's lives, and purpose-led brand messaging.",
    results: ["Strong organic brand awareness", "High audience relatability", "Clear market positioning", "Trust via storytelling"],
    fullResults: [
      { num: "0→∞", label: "Brand Built from Scratch" },
      { num: "100%", label: "Organic Growth" },
      { num: "Trust", label: "Built via Storytelling" },
      { num: "Unique", label: "Market Positioning" },
    ],
    challenge: "Seasuvai is a karuvadu brand in a traditionally unorganized category. The challenge: create awareness from scratch, build trust where most sellers only sell, and educate people about the origin and process of seafood.",
    strategy: "Content-first organic strategy built on 3 pillars: Visual Representation (fishermen & sourcing), Purpose-Driven Storytelling (founder mission), and Problem-Based Content (addressing consumer trust issues).",
    steps: [
      "Showcased real visuals of fishermen, working environments, and the journey of seafood from source to plate — making content educational and unique.",
      "Used founder Meshwin's story to communicate brand mission and build emotional connection with the audience.",
      "Created content addressing consumer problems: lack of quality trust, no sourcing clarity, hygiene concerns — building relatability.",
    ],
    tags: ["Content Strategy", "Social Media Marketing", "Brand Positioning", "Storytelling"],
  },
  {
    id: 4,
    logo: "/elite.png",
    client: "Elite Fitness Studio",
    project: "New Year Campaign",
    industry: "Fitness",
    industryClass: "industry-fitness",
    description: "Generated ₹6 Lakhs+ revenue in just 30 days through a hybrid offline + online conversion funnel for a gym's New Year offer campaign.",
    results: ["₹6L+ revenue in 30 days", "5 banners + 300 posters offline", "8 strategic videos created", "High profile-to-conversion rate"],
    fullResults: [
      { num: "₹6L+", label: "Revenue in 30 Days" },
      { num: "8", label: "Strategic Videos" },
      { num: "300", label: "Posters Distributed" },
      { num: "5", label: "Premium Banner Spots" },
    ],
    challenge: "Elite Fitness had a competitively priced New Year offer. The challenge: ensure the offer doesn't just reach people — it converts them. Generate ₹6L+ in revenue within one month.",
    strategy: "Hybrid approach: Offline Visibility (local dominance) → Online Conversion Funnel (awareness → interest → trust → action), with Meta Ads scaling reach.",
    steps: [
      "Offline: 5 banners in premium areas + 300 posters across city to create local brand dominance.",
      "Online: 8 videos (2 announcements + 3 awareness + 1 UGC trust + 2 public reach) driving profile traffic.",
      "Conversion: Meta Ads scaling → Announcement video (50% decision influence) → UGC trust video → Proposal video with clear offer value proposition.",
    ],
    tags: ["Marketing Strategy", "Social Media Marketing", "Content Creation", "Meta Ads", "Offline Campaigns"],
  },
];

const industries = [
  { icon: "/rs.svg", name: "Real Estate", desc: "Marketing strategies for property developers and real estate projects, including awareness campaigns, lead generation funnels, and pre-launch promotions." },
  { icon: "/jw.svg", name: "Jewellery & Retail", desc: "Helping jewellery brands and retail businesses build strong brand visibility and attract customers through creative content and targeted advertising." },
  { icon: "/hc.svg", name: "Healthcare & Hospitals", desc: "Digital marketing for pharmacies, clinics, and healthcare centres to improve local awareness and connect with patients." },
  { icon: "/ht.svg", name: "Restaurants & Hotels", desc: "Helping restaurants and food businesses increase footfall and brand recognition through engaging content and location-based marketing." },
  { icon: "/ec.svg", name: "E-commerce Brands", desc: "Helping online stores grow through product marketing, performance advertising, conversion-focused content, and customer acquisition strategies." },
  { icon: "/pb.svg", name: "Personal Brands", desc: "Supporting creators, entrepreneurs, and professionals in building authority, growing their audience, and monetizing their personal brand online." },
];

const services = [
  { icon: "/smm.svg", name: "Social Media Marketing", desc: "We help brands build a strong presence on platforms like Instagram and Facebook through strategic content planning, creative execution, and consistent audience engagement." },
  { icon: "/fb ad.svg", name: "Performance Marketing (Meta Ads)", desc: "We run targeted advertising campaigns designed to generate leads, sales, and measurable business results using data-driven Meta advertising strategies." },
  { icon: "/content creator.svg", name: "Content Creation", desc: "Our team produces high-quality photo and video content that helps brands stand out, communicate their message clearly, and connect with their audience." },
  { icon: "/branding.svg", name: "Branding & Digital Presence", desc: "We help businesses establish a strong brand identity and build a professional digital presence that creates trust with customers." },
  { icon: "/lead generation.svg", name: "Lead Generation Funnels", desc: "We design marketing funnels that convert audience attention into qualified leads using landing pages, WhatsApp funnels, and targeted campaigns." },
  { icon: "/web development.svg", name: "Web Development", desc: "We build professional websites and landing pages that help businesses showcase their services, generate leads, and improve their online presence." },
  { icon: "/personal brand.svg", name: "Personal Brand Growth", desc: "We help entrepreneurs, creators, and professionals build authority online through strategic content, audience growth, and personal branding." },
];

const testimonials = [
  { initials: "KV", name: "Kudil VIP Garden", role: "Real Estate Developer", text: "CTF's funnel strategy was remarkable. We didn't just get leads — we got serious buyers. Selling 33 out of 35 plots in 2 months exceeded every expectation.", stars: 5 },
  { initials: "JC", name: "Jeyaraj & Co", role: "Hypermarket Owner", text: "Even with D-Mart opening next door, CTF made sure our launch was the talk of the town. The local connection they built was extraordinary.", stars: 5 },
  { initials: "MS", name: "Meshwin, Seasuvai", role: "Founder, Seafood Brand", text: "They didn't just market my product — they told my story. The content strategy they built gave Seasuvai a brand identity I'm truly proud of.", stars: 5 },
  { initials: "EF", name: "Elite Fitness Studio", role: "Gym Owner", text: "₹6 lakhs in 30 days. I was skeptical at first, but their structured approach — offline + online combined — delivered results beyond what I imagined.", stars: 5 },
  { initials: "AR", name: "A. Ramesh", role: "E-commerce Entrepreneur", text: "Creative Tech Factory understands the local market incredibly well. Their insights and execution are on another level compared to any agency I've worked with.", stars: 5 },
  { initials: "PK", name: "Priya K.", role: "Healthcare Clinic Owner", text: "Within weeks of working with CTF, our local awareness jumped significantly. Patients started mentioning finding us online. Highly recommend.", stars: 5 },
];

const videos = [
  { url: "https://www.instagram.com/reels/DVvbJ2igd1x/", thumb: "/jthumb.png", name: "Jeyaraj & Co", role: "Hypermarket" },
  { url: "https://www.instagram.com/reels/DRwvFkVgUGO/", thumb: "/ethumb.png", name: "Envoholic", role: "Bio-Tech" },
  { url: "https://www.instagram.com/reels/DKD-7nTh2NW/", thumb: "/sthumb.png", name: "Stylish Tamilan", role: "Fashion" },
  { url: "https://www.instagram.com/reels/DJgQ7yQBhc2/", thumb: "/nthumb.png", name: "Nakshtra", role: "Art Gallery" },
  { url: "https://www.instagram.com/reels/DJ1qMAgh4Yp/", thumb: "/vthumb.png", name: "VPK Foods", role: "Sweets & Snacks" },
];

const marqueeItems = ["Social Media Marketing", "Meta Ads", "Content Creation", "Lead Generation", "Branding", "Web Development", "Personal Brand Growth", "Performance Marketing"];

function HeroCanvas({ mouseRef }) {
  const canvasRef = useRef();
  const frameRef = useRef();
  const ripplesRef = useRef([]);
  const lastAutoRef = useRef(0);
  const lastMouseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const CELL = 60;

    const resize = () => {
      const p = canvas.parentElement;
      canvas.width = p.offsetWidth;
      canvas.height = p.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);

    const spawn = (x, y, time) => {
      ripplesRef.current.push({ x, y, born: time });
    };

    const draw = (time) => {
      const W = canvas.width, H = canvas.height;
      const { x: mx, y: my, active } = mouseRef.current;
      ctx.clearRect(0, 0, W, H);

      /* base grid */
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(255,255,255,0.07)";
      for (let x = 0; x <= W + CELL; x += CELL) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = 0; y <= H + CELL; y += CELL) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      /* auto-spawn ripples at random intersections */
      if (time - lastAutoRef.current > 900) {
        const rx = Math.floor(Math.random() * Math.ceil(W / CELL)) * CELL;
        const ry = Math.floor(Math.random() * Math.ceil(H / CELL)) * CELL;
        spawn(rx, ry, time);
        lastAutoRef.current = time;
      }

      /* mouse-triggered ripples */
      if (active && mx >= 0 && my >= 0 && time - lastMouseRef.current > 350) {
        spawn(Math.round(mx / CELL) * CELL, Math.round(my / CELL) * CELL, time);
        lastMouseRef.current = time;
      }

      /* draw ripples */
      ripplesRef.current = ripplesRef.current.filter(rp => {
        const age = (time - rp.born) / 1000;
        if (age > 1) return false;
        ctx.strokeStyle = `rgba(96,170,255,${(1 - age) * 0.55})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(rp.x, rp.y, age * 100, 0, Math.PI * 2); ctx.stroke();
        return true;
      });

      frameRef.current = requestAnimationFrame(draw);
    };
    frameRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(frameRef.current); ro.disconnect(); };
  }, [mouseRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return <canvas ref={canvasRef} className="hero-grid-canvas" />;
}

function useIntersection(ref, options = {}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setIsIntersecting(true); obs.disconnect(); }
    }, { threshold: 0.15, ...options });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return isIntersecting;
}


function CountUp({ target, suffix = "", prefix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  const vis = useIntersection(ref);
  useEffect(() => {
    if (!vis) return;
    const num = parseFloat(target.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) { setVal(target); return; }
    let start = 0; const step = num / 40;
    const id = setInterval(() => {
      start += step;
      if (start >= num) { setVal(num % 1 === 0 ? num : num.toFixed(1)); clearInterval(id); }
      else setVal(num % 1 === 0 ? Math.floor(start) : start.toFixed(1));
    }, 30);
    return () => clearInterval(id);
  }, [vis]);
  return <span ref={ref}>{prefix}{val}{suffix}</span>;
}

/* Premium smooth scroll with ease-out-expo easing */
function smoothScrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const target = el.getBoundingClientRect().top + window.scrollY;
  const start = window.scrollY;
  const duration = 920;
  const t0 = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 4);
  const tick = (now) => {
    const prog = Math.min((now - t0) / duration, 1);
    window.scrollTo(0, start + (target - start) * ease(prog));
    if (prog < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/* AOS-style scroll-reveal with direction variety */
function Anim({ children, from = "bottom", delay = 0, dur = 0.8, style: s = {}, className = "" }) {
  const ref = useRef();
  const vis = useIntersection(ref, { threshold: 0.1 });
  const hidden = {
    bottom: { opacity: 0, transform: "translateY(52px)" },
    left:   { opacity: 0, transform: "translateX(-64px)" },
    right:  { opacity: 0, transform: "translateX(64px)" },
    scale:  { opacity: 0, transform: "scale(0.8)" },
    flip:   { opacity: 0, transform: "perspective(700px) rotateX(24deg) translateY(36px)" },
    fade:   { opacity: 0, transform: "none" },
  }[from] || { opacity: 0, transform: "translateY(52px)" };
  return (
    <div
      ref={ref}
      className={`anim-wrap ${className}`}
      style={{
        transition: `opacity ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...(vis ? { opacity: 1, transform: "none" } : hidden),
        ...s,
      }}
    >
      {children}
    </div>
  );
}

/* Extract shortcode from any Instagram URL and return the embed src */
function toEmbedUrl(url) {
  const m = url.match(/\/(reel|reels|p)\/([A-Za-z0-9_-]+)/);
  return m ? `https://www.instagram.com/p/${m[2]}/embed/` : url;
}

/* Fetch the oEmbed thumbnail for one Instagram URL */
async function fetchThumb(url) {
  try {
    const res = await fetch(
      `https://api.instagram.com/oembed/?url=${encodeURIComponent(url)}&maxwidth=400`
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.thumbnail_url || null;
  } catch { return null; }
}

export default function CTFPortfolio() {
  const [activeCase, setActiveCase] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [ctaOpen, setCtaOpen] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const heroRef = useRef();
  const heroMouseRef = useRef({ x: -1, y: -1, active: false });

  useEffect(() => {
    videos.forEach((v, i) => {
      if (v.thumb) { setThumbs(t => ({ ...t, [i]: v.thumb })); return; }
      fetchThumb(v.url).then(url => { if (url) setThumbs(t => ({ ...t, [i]: url })); });
    });
  }, []);

  useEffect(() => {
    document.body.style.overflow = (activeCase || activeVideo || ctaOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeCase, activeVideo, ctaOpen]);

  return (
    <>
      <style>{style}</style>

      {/* HERO */}
      <section
        className="hero"
        id="hero"
        ref={heroRef}
        onMouseMove={(e) => {
          const rect = heroRef.current.getBoundingClientRect();
          heroMouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top, active: true };
        }}
        onMouseLeave={() => { heroMouseRef.current = { x: -1, y: -1, active: false }; }}
      >
        <div className="hero-bg-text">CTF</div>
        <HeroCanvas mouseRef={heroMouseRef} />
        <div className="hero-orb1" /><div className="hero-orb2" />
        <div className="hero-content">
          <div className="hero-eyebrow">
            <div className="hero-eyebrow-dot" />
            <span>Digital Marketing Agency</span>
          </div>
          <h1 className="hero-title">
            Real Marketing.<br />
            Real Strategy.<br />
            <span className="accent">Real Results.</span>
          </h1>
          <p className="hero-sub">
            See how Creative Tech Factory has helped businesses increase visibility, generate leads, and build strong brands through data-driven digital marketing campaigns.
          </p>
          <div className="hero-btns">
            <button className="btn-primary" onClick={() => smoothScrollTo("work")}>See Our Work</button>
            <button className="btn-secondary" onClick={() => setCtaOpen(true)}>Work with Us</button>
          </div>
          <p className="hero-trust">Trusted by businesses across multiple industries</p>
        </div>
        {/* <div className="hero-scroll"> */}
          {/* <span>Scroll</span> */}
          {/* <div className="scroll-line" />
        </div> */}
      </section>

      {/* MARQUEE */}
      <div className="marquee-section">
        <div className="marquee-track">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <div key={i} className="marquee-item">
              <div className="marquee-sep" />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section style={{ padding: "80px 5% 0" }}>
        <Anim from="scale">
          <div className="stats-row">
            {[
              { num: "10", suf: "k", label: "Reach in Month" },
              { num: "5", suf: "M+", label: "Video Views in a Month" },
              { num: "6", pre: "₹", suf: "L+", label: "Revenue in 30 Days" },
              { num: "4", suf: "+", label: "Industries Served" },
            ].map((s, i) => (
              <div className="stat-item" key={i}>
                <div className="stat-num">
                  {s.pre || ""}<CountUp target={s.num + ""} />{s.suf}
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </Anim>
      </section>

      {/* CASE STUDIES */}
      <section id="work" style={{ padding: "100px 5%" }}>
        <Anim from="bottom">
          <span className="section-label">Our Work</span>
          <h2 className="section-title">Our Work &<br />Client Success Stories</h2>
          <p className="section-sub">Explore how Creative Tech Factory has helped businesses across different industries grow through strategic marketing, powerful content, and performance-driven campaigns.</p>
        </Anim>
        <div className="case-grid">
          {caseStudies.map((cs, i) => (
            <Anim key={cs.id} delay={i * 0.12} from={i % 2 === 0 ? "left" : "right"}>
              <div className="case-card">
                <div className="case-card-top">
                  <div className="case-card-number">0{cs.id}</div>
                  <span className={`case-card-industry ${cs.industryClass}`}>{cs.industry}</span>
                  <div className="case-card-title">{cs.project}</div>
                  <p className="case-card-desc">{cs.description}</p>
                  <div className="case-results">
                    {cs.results.slice(0, 3).map((r, ri) => (
                      <div className="case-result-item" key={ri}>
                        <div className="case-result-icon">
                          <svg viewBox="0 0 10 10" fill="white"><polyline points="2,5 4,8 8,2" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </div>
                        {r}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="case-card-bottom">
                  <div className="case-client">
                    <img src={cs.logo} alt={cs.client} className="case-client-logo" />
                  </div>
                  <button className="case-view-btn" onClick={() => setActiveCase(cs)}>View Case Study</button>
                </div>
              </div>
            </Anim>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="industries-section" id="industries">
        <Anim from="bottom">
          <span className="section-label">Industries</span>
          <h2 className="section-title">Industries We Work With</h2>
          <p className="section-sub">Creative Tech Factory works with businesses across multiple industries, helping them build strong brands, attract the right audience, and generate consistent growth through digital marketing.</p>
        </Anim>
        <div className="industry-grid">
          {industries.map((ind, i) => (
            <Anim key={i} delay={i * 0.08} from="flip">
              <div className="industry-card">
                <div className="industry-icon">
                  <img src={ind.icon} alt={ind.name} />
                </div>
                <div className="industry-name">{ind.name}</div>
                <p className="industry-desc">{ind.desc}</p>
              </div>
            </Anim>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials-section" id="about">
        <Anim from="fade">
          <span className="section-label">Testimonials</span>
          <h2 className="section-title">What Our Clients Say</h2>
          <p className="section-sub">Real results from real businesses. Watch client video testimonials and read what our partners say about working with Creative Tech Factory.</p>
        </Anim>

        {/* ── VIDEO ROW ── */}
        <div className="video-row">
          {videos.map((v, i) => (
            <div key={i} className="video-card" onClick={() => setActiveVideo(v)}>
              <div className="video-thumb">
                {thumbs[i]
                  ? <img src={thumbs[i]} alt={v.name} />
                  : <div className="video-thumb-placeholder">
                      <svg className="video-insta-icon" width="40" height="40" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="2" width="20" height="20" rx="5" stroke="white" strokeWidth="1.5"/>
                        <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5"/>
                        <circle cx="17.5" cy="6.5" r="1" fill="white"/>
                      </svg>
                    </div>
                }
                <div className="video-play-overlay">
                  <div className="video-play-circle">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill={BRAND}>
                      <path d="M6 4l11 6-11 6V4z"/>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="video-card-info">
                <div className="video-card-name">{v.name}</div>
                <div className="video-card-role">{v.role}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── VIDEO MODAL ── */}
        {activeVideo && (
          <div
            className="video-modal-overlay"
            onClick={(e) => e.target === e.currentTarget && setActiveVideo(null)}
          >
            <div className="video-modal">
              <button className="video-modal-close" onClick={() => setActiveVideo(null)}>×</button>
              <iframe
                className="video-modal-frame"
                src={toEmbedUrl(activeVideo.url)}
                title={activeVideo.name}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* ── GOOGLE REVIEW MARQUEE ── */}
        <div className="review-marquee-outer">
          <div className="review-marquee-track">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div key={i} className="review-card">
                <div className="review-card-top">
                  <div className="review-card-author">
                    <div className="review-card-avatar">{t.initials}</div>
                    <div>
                      <div className="review-card-name">{t.name}</div>
                      <div className="review-card-source">
                                        <div className="review-stars">
                  {Array(t.stars).fill(0).map((_, si) => <span key={si} className="review-star">★</span>)}
                </div>
                  </div>
                    </div>
                  </div>
                </div>
                <p className="review-text">"{t.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" style={{ padding: "100px 5%" }}>
        <Anim from="bottom">
          <span className="section-label">What We Do</span>
          <h2 className="section-title">Our Core Services</h2>
          <p className="section-sub">Creative Tech Factory provides strategic digital marketing solutions designed to help businesses build strong brands, attract the right audience, and generate consistent growth.</p>
        </Anim>
        <div className="services-grid">
          {services.map((s, i) => (
            <Anim key={i} delay={i * 0.07} from="bottom">
              <div className="service-card">
                {/* <div className="service-number">{String(i + 1).padStart(2, "0")}</div> */}
                <div className="service-icon">
                  <img src={s.icon} alt={s.name} />
                </div>
                <div className="service-name">{s.name}</div>
                <p className="service-desc">{s.desc}</p>
                <div className="service-line" />
              </div>
            </Anim>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" id="cta">
        <Anim from="scale">
          <h2 className="cta-title">Ready to Grow<br />Your Brand?</h2>
          <p className="cta-sub">Let's discuss how Creative Tech Factory can help your business achieve better visibility, leads, and growth.</p>
          <button className="cta-btn" onClick={() => setCtaOpen(true)}>Get in Touch</button>
        </Anim>
      </section>

      {/* CASE STUDY MODAL */}
      {activeCase && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setActiveCase(null)}>
          <div className="modal-content">
            <div className="modal-header">
              <button className="modal-close" onClick={() => setActiveCase(null)}>×</button>
              <div className="modal-industry-tag">{activeCase.industry} · Case Study</div>
              <div className="modal-title">{activeCase.project}</div>
              <div className="modal-client">Client: {activeCase.client}</div>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <div className="modal-section-title">Results</div>
                <div className="modal-results">
                  {activeCase.fullResults.map((r, i) => (
                    <div className="modal-result-item" key={i}>
                      <div className="modal-result-num">{r.num}</div>
                      <div className="modal-result-label">{r.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-section">
                <div className="modal-section-title">The Challenge</div>
                <p className="modal-text">{activeCase.challenge}</p>
              </div>
              <div className="modal-section">
                <div className="modal-section-title">Our Strategy</div>
                <p className="modal-text">{activeCase.strategy}</p>
              </div>
              <div className="modal-section">
                <div className="modal-section-title">Execution</div>
                <div className="modal-steps">
                  {activeCase.steps.map((step, i) => (
                    <div className="modal-step" key={i}>
                      <div className="modal-step-num">{i + 1}</div>
                      <p className="modal-step-text">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-section">
                <div className="modal-section-title">Services Provided</div>
                <div className="modal-tags">
                  {activeCase.tags.map((tag, i) => <span className="modal-tag" key={i}>{tag}</span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA POPUP */}
      {ctaOpen && (
        <div className="cta-popup-overlay" onClick={(e) => e.target === e.currentTarget && setCtaOpen(false)}>
          <div className="cta-popup">
            <button className="cta-popup-close" onClick={() => setCtaOpen(false)}>×</button>
            <div className="cta-popup-icon-wrap">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            <h2 className="cta-popup-heading">Say Hi 👋</h2>
            <p className="cta-popup-sub">Start your journey with Creative Tech Factory. Let's chat about growing your brand!</p>
            <div className="cta-popup-badges">
              <span className="cta-popup-badge">Free Consultation</span>
              <span className="cta-popup-badge">Quick Response</span>
              <span className="cta-popup-badge">No Commitment</span>
            </div>
            <a
              href="https://wa.me/+916379906325"
              target="_blank"
              rel="noopener noreferrer"
              className="wa-cta-btn"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Chat on WhatsApp
            </a>
          </div>
        </div>
      )}
    </>
  );
}
